import type { MaterialsFolder } from "@/lib/materials-api";
import type { MaterialsPageContext } from "@/lib/materials-folder-tree";

/** 移动目标中应禁用的文件夹 id（自身 + 全部子孙，防环） */
export function disabledTargetFolderIds(
  selectedFolderIds: number[],
  folders: MaterialsFolder[],
): Set<number> {
  const byParent = new Map<number | null, MaterialsFolder[]>();
  for (const folder of folders) {
    const key = folder.parent_id;
    const list = byParent.get(key) ?? [];
    list.push(folder);
    byParent.set(key, list);
  }

  const disabled = new Set<number>();
  const stack = [...selectedFolderIds];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (disabled.has(id)) continue;
    disabled.add(id);
    for (const child of byParent.get(id) ?? []) {
      stack.push(child.id);
    }
  }
  return disabled;
}

/** 移动后目标是否仍落在当前工作区（由页面 context 决定，而非 scope 筛选） */
export function remainsInWorkspace(
  context: MaterialsPageContext,
  targetFolderId: number | null,
  workRootId: number | null,
  folders: MaterialsFolder[],
): boolean {
  if (context.kind === "enterprise") return true;

  const target =
    targetFolderId == null
      ? folders.find((folder) => folder.id === workRootId)
      : folders.find((folder) => folder.id === targetFolderId);
  if (!target) return true;

  if (context.kind === "brand") {
    const brandId = context.brandId ? Number(context.brandId) : null;
    return brandId != null && target.brand_id === brandId;
  }

  const siteId = context.brandSiteId ? Number(context.brandSiteId) : null;
  return siteId != null && target.brand_site_id === siteId;
}

export function moveSuccessToast(count: number, leftCurrentWorkspace: boolean): string {
  if (count === 0) {
    return "已在目标位置，无需移动";
  }
  if (leftCurrentWorkspace) {
    return `已移动 ${count} 项（已不在当前工作区，可在对应品牌或站点素材库查看）`;
  }
  return `已移动 ${count} 项`;
}

/** 选中项是否已全部位于目标目录（同位置移动 = 无需请求） */
export function isMoveNoOp(
  itemIds: number[],
  folderIds: number[],
  targetFolderId: number | null,
  items: { id: number; folder_id: number | null }[],
  folders: { id: number; parent_id: number | null }[],
): boolean {
  if (itemIds.length === 0 && folderIds.length === 0) return true;

  const itemsOk =
    itemIds.length === 0 ||
    itemIds.every((id) => {
      const item = items.find((row) => row.id === id);
      return item != null && item.folder_id === targetFolderId;
    });

  const foldersOk =
    folderIds.length === 0 ||
    folderIds.every((id) => {
      const folder = folders.find((row) => row.id === id);
      return folder != null && folder.parent_id === targetFolderId;
    });

  return itemsOk && foldersOk;
}
